import React, { createContext, useContext, useState, useEffect } from 'react';

interface Settings {
  timer: {
    pomodoro: number;
    shortBreak: number;
    longBreak: number;
    autoStartBreaks: boolean;
    autoStartPomodoros: boolean;
    longBreakInterval: number;
    tickingSound: 'none' | 'soft' | 'mechanical' | 'digital';
    hourFormat: '12' | '24';
  };
  goals: {
    autoUpdateGoals: boolean;
    countBreakTime: boolean;
    showMilestoneNotifications: boolean;
    autoAdjustMilestones: boolean;
  };
  appearance: {
    theme: 'light' | 'dark';
    compactMode: boolean;
  };
  accessibility: {
    reducedMotion: boolean;
    highContrast?: boolean;
    largeText?: boolean;
  };
  notifications: {
    enabled: boolean;
    sound: boolean;
    desktop: boolean;
    email: boolean;
    breakReminders?: boolean;
    taskReminders?: boolean;
  };
  sound: {
    enabled: boolean;
    volume: number;
    workSound?: string;
    breakSound?: string;
  };
  focus: {
    autoEnable: boolean;
    blockSites: boolean;
  };
  language: string;
  privacy: {
    shareActivity: boolean;
    communityVisibility: 'public' | 'friends' | 'private';
  };
  navigation: {
    showGoals: boolean;
    showCommunity: boolean;
    showQuotes: boolean;
    showAIAssistant: boolean;
    showMusicPlayer: boolean;
  };
  pomodoro: {
    lastSelectedTaskId: string | null;
  };
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (updates: Partial<Settings>) => void;
  resetSettings?: () => void;
}

const defaultSettings: Settings = {
  timer: {
    pomodoro: 60,
    shortBreak: 5,
    longBreak: 15,
    autoStartBreaks: true,
    autoStartPomodoros: false,
    longBreakInterval: 4,
    tickingSound: 'none',
    hourFormat: '24',
  },
  goals: {
    autoUpdateGoals: false,
    countBreakTime: false,
    showMilestoneNotifications: false,
    autoAdjustMilestones: false,
  },
  appearance: {
    theme: 'light',
    compactMode: false,
  },
  accessibility: {
    reducedMotion: false,
    highContrast: false,
    largeText: false,
  },
  notifications: {
    enabled: true,
    sound: true,
    desktop: true,
    email: false,
  },
  sound: {
    enabled: true,
    volume: 0.5,
  },
  focus: {
    autoEnable: false,
    blockSites: false,
  },
  language: 'en',
  privacy: {
    shareActivity: true,
    communityVisibility: 'public',
  },
  navigation: {
    showGoals: false,
    showCommunity: false,
    showQuotes: false,
    showAIAssistant: false,
    showMusicPlayer: false,
  },
  pomodoro: {
    lastSelectedTaskId: null,
  },
};

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const savedSettings = localStorage.getItem('settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return {
          ...defaultSettings,
          ...parsed,
          timer: { ...defaultSettings.timer, ...parsed.timer },
          notifications: { ...defaultSettings.notifications, ...parsed.notifications },
          sound: { ...defaultSettings.sound, ...parsed.sound },
          appearance: { ...defaultSettings.appearance, ...parsed.appearance },
          accessibility: { ...defaultSettings.accessibility, ...parsed.accessibility },
          focus: { ...defaultSettings.focus, ...parsed.focus },
          privacy: { ...defaultSettings.privacy, ...parsed.privacy },
          navigation: { ...defaultSettings.navigation, ...parsed.navigation },
          pomodoro: { ...defaultSettings.pomodoro, ...parsed.pomodoro },
          goals: { ...defaultSettings.goals, ...parsed.goals },
        };
      }
      return defaultSettings;
    } catch (error) {
      console.error('Error loading settings:', error);
      return defaultSettings;
    }
  });

  // Apply accessibility settings to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('high-contrast', settings.accessibility.highContrast || false);
    root.classList.toggle('large-text', settings.accessibility.largeText || false);
    root.classList.toggle('reduced-motion', settings.accessibility.reducedMotion || false);
  }, [settings.accessibility]);

  const updateSettings = (updates: Partial<Settings>) => {
    setSettings(prev => {
      try {
        const newSettings = {
          ...prev,
          ...updates,
          timer: updates.timer ? { ...prev.timer, ...updates.timer } : prev.timer,
          notifications: updates.notifications ? { ...prev.notifications, ...updates.notifications } : prev.notifications,
          sound: updates.sound ? { ...prev.sound, ...updates.sound } : prev.sound,
          appearance: updates.appearance ? { ...prev.appearance, ...updates.appearance } : prev.appearance,
          accessibility: updates.accessibility ? { ...prev.accessibility, ...updates.accessibility } : prev.accessibility,
          focus: updates.focus ? { ...prev.focus, ...updates.focus } : prev.focus,
          privacy: updates.privacy ? { ...prev.privacy, ...updates.privacy } : prev.privacy,
          navigation: updates.navigation ? { ...prev.navigation, ...updates.navigation } : prev.navigation,
          pomodoro: updates.pomodoro ? { ...prev.pomodoro, ...updates.pomodoro } : prev.pomodoro,
          goals: updates.goals ? { ...prev.goals, ...updates.goals } : prev.goals,
        };
        localStorage.setItem('settings', JSON.stringify(newSettings));
        return newSettings;
      } catch (error) {
        console.error('Error saving settings:', error);
        return prev;
      }
    });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.setItem('settings', JSON.stringify(defaultSettings));
  };

  const value = React.useMemo(() => ({
    settings,
    updateSettings,
    resetSettings,
  }), [settings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};